import * as Sentry from '@sentry/react'
import { useCallback, useState } from 'react'
import { z } from 'zod'

import { TimeGranularity } from '@common/util/enums'
import { PriceRecommendationChart } from '~/components/properties/hooks/priceRecommendationChart'
import { PropertySummaryChart } from '~/components/properties/hooks/summaryViewChart'
import { MarketRentAdjustmentRemovalType, PricingUpdateType } from '~/graphql/generated/query.types'

export enum LocalStorageKey {
  ADMIN_DATA = 'adminData',
  ADMIN_PRICE_RECOMMENDATIONS_TAB = 'adminPriceRecommendationsTab',
  ADMIN_WRITE_MODE = 'adminWriteMode',
  CONVERSION_WINDOW_DAYS = 'conversionWindowDays',
  LEASES_PAGE_TAB = 'leasesPageTab',
  CURRENT_MARKET_POSITION_CHART_OPTIONS = 'todaysMarketPositionChartOptions',
  OVERVIEW_VIEW = 'overviewView',
  PORTFOLIO_SID = 'portfolioSid',
  PRICING_REVIEW_DATA = 'pricingReviewData',
  PRICING_VIEW_GROUPING_LEVEL = 'pricingViewGroupingLevel',
  PRICE_RECOMMENDATION_CHARTS_TOGGLE = 'priceRecommendationChartsToggle',
  PROPERTY_AMENITIES_TAB = 'propertyAmenitiesTab',
  PROPERTY_SID = 'propertySid',
  PROSPECTIVE_PROPERTY_SID = 'prospectivePropertySid',
  PROPERTY_VIEW = 'propertyView',
  PROPERTY_SETTINGS_VIEW = 'propertySettingsView',
  PROPERTY_SUMMARY_CHARTS = 'propertySummaryCharts',
  RENEWALS_DATE_RANGE = 'renewalsDateRange', // YYYY-MM-DD,YYYY-MM-DD\
  RENEWAL_REQUESTS_ASSIGNEE_TAB = 'renewalRequestsAssigneeTab',
  RENEWAL_REQUESTS_STATE_TAB = 'renewalRequestsStateTab',
  RENEWAL_SUMMARY_DATE_GROUPING = 'renewalSummaryDateGrouping',
  RENEWALS_PROPERTY_SID = 'renewalsPropertySid',
  RENEWALS_VIEW = 'renewalsView',
  WATCHLIST_VIEW = 'watchlistView',
  REPORTS_VIEW = 'reportsView',
  SIDEBAR_NAV_COLLAPSED = 'sidebarNavCollapsed',
  PRICE_HISTORY_CHART_METRICS = 'priceHistoryChartMetrics',
  UNIT_AVAILABILITY_CHART_GROUP_BY = 'unitAvailabilityChartGroupBy',
  UNIT_AVAILABILITY_CHART_DISPLAY_TYPE = 'unitAvailabilityChartDisplayType',
  OCCUPANCY_CHART_OPTIONS = 'occupancyChartOptions',
  PROPERTY_STATS_AGGREGATION_WINDOW_DAYS = 'propertyStatsAggregationWindowDays',
  COMPS_WEEKLY_INVENTORY_COMPS_AGGREGATED = 'compsWeeklyInventoryCompsAggregated',
  COMPS_WEEKLY_INVENTORY_DATE_RANGE_DAYS = 'compsWeeklyInventoryDateRangeDays',
  COMPS_MARKET_POSITION_ARE_COMPS_AGGREGATED = 'compsMarketPositionAreCompsAggregated',
  COMPS_MARKET_POSITION_DATE_RANGE_DAYS = 'compsMarketPositionDateRangeDays',
  MARKET_POSITION_CHART_COMPARE_MODE = 'marketPositionChartCompareMode',
  COMPS_OCCUPANCY_COMPS_AGGREGATED = 'compsOccupancyCompsAggregated',
  COMPS_OCCUPANCY_DATE_RANGE_DAYS = 'compsOccupancyDateRangeDays',
  COMPS_SPECIALS_TIMELINE_DATE_RANGE_DAYS = 'compsSpecialsTimelineDateRangeDays',
  DEMAND_CHART_OPTIONS = 'demandChartOptions',
  RENEWALS_CHART_OPTIONS = 'renewalsChartOptions',
  UPCOMING_RENEWALS_CHART_OPTIONS = 'upcomingRenewalsChartOptions',
  CONVERSION_CHART_OPTIONS = 'conversionChartOptions',
  RENT_CHART_OPTIONS = 'rentChartOptions',
  PRICE_HISTORY_RENT_CHART_OPTIONS = 'priceHistoryRentChartOptions',
  NEVER = 'never',
  INCLUDE_DISABLED_PROPERTIES = 'includeDisabledProperties',
  INCLUDE_HIDDEN_PROPERTIES = 'includeHiddenProperties',
  OKTA_CLIENT_ID = 'oktaClientId',
  OKTA_DOMAIN = 'oktaDomain',
  OKTA_EXPECTED_EMAIL = 'oktaExpectedEmail',
  LEAD_SOURCE_BREAKDOWN_USE_RAW = 'leadSourceBreakdownUseRaw',
  CHAT_DOCK_MODE = 'chatDockMode',
  CHAT_DOCK_WIDTH = 'chatDockWidth',
  /** Demo-only: optional per-browser override for the Ask Rentana UI label. */
  ASK_RENTANA_LABEL_OVERRIDE = 'askRentanaLabelOverride',
}

const chartOptionsSchema = z.object({
  displayType: z.enum(['line', 'bar']),
  metrics: z.array(z.string()),
  groupBy: z.nativeEnum(TimeGranularity).optional(),
  movingAveragePeriod: z.enum(['none', '7d', '14d', '30d', '60d']).optional(),
  conversionWindowDays: z.enum(['14d', '30d', '60d']).optional(),
})

export const localStorageKeyToSchema = {
  [LocalStorageKey.ADMIN_DATA]: z.string(),
  [LocalStorageKey.ADMIN_PRICE_RECOMMENDATIONS_TAB]: z.string(),
  [LocalStorageKey.ADMIN_WRITE_MODE]: z.string(),
  [LocalStorageKey.CONVERSION_WINDOW_DAYS]: z.number(),
  [LocalStorageKey.LEASES_PAGE_TAB]: z.string(),
  [LocalStorageKey.CURRENT_MARKET_POSITION_CHART_OPTIONS]: z.array(
    z.enum(['highest', 'average', 'best', 'subject-highest', 'subject-average', 'subject-best'])
  ),
  [LocalStorageKey.OVERVIEW_VIEW]: z.string(),
  [LocalStorageKey.PORTFOLIO_SID]: z.string(),
  [LocalStorageKey.PRICING_VIEW_GROUPING_LEVEL]: z.string(),
  [LocalStorageKey.PRICE_RECOMMENDATION_CHARTS_TOGGLE]: z.object(
    Object.values(PriceRecommendationChart).reduce(
      (acc, chart) => ({ ...acc, [chart]: z.boolean().optional() }),
      {} as Record<PriceRecommendationChart, z.ZodOptional<z.ZodBoolean>>
    )
  ),
  [LocalStorageKey.PROPERTY_AMENITIES_TAB]: z.string(),
  [LocalStorageKey.PROPERTY_SID]: z.string(),
  [LocalStorageKey.PROSPECTIVE_PROPERTY_SID]: z.string(),
  [LocalStorageKey.PROPERTY_VIEW]: z.string(),
  [LocalStorageKey.PROPERTY_SETTINGS_VIEW]: z.string(),
  [LocalStorageKey.PROPERTY_SUMMARY_CHARTS]: z.object({
    chartOrder: z.array(z.nativeEnum(PropertySummaryChart)),
    hiddenCharts: z.record(z.nativeEnum(PropertySummaryChart), z.boolean()),
  }),
  [LocalStorageKey.RENEWALS_DATE_RANGE]: z.string(),
  [LocalStorageKey.RENEWAL_REQUESTS_ASSIGNEE_TAB]: z.string(),
  [LocalStorageKey.RENEWAL_REQUESTS_STATE_TAB]: z.string(),
  [LocalStorageKey.RENEWAL_SUMMARY_DATE_GROUPING]: z.string(),
  [LocalStorageKey.RENEWALS_VIEW]: z.string(),
  [LocalStorageKey.WATCHLIST_VIEW]: z.string(),
  [LocalStorageKey.RENEWALS_PROPERTY_SID]: z.string(),
  [LocalStorageKey.REPORTS_VIEW]: z.string(),
  [LocalStorageKey.SIDEBAR_NAV_COLLAPSED]: z.boolean(),
  [LocalStorageKey.PRICE_HISTORY_CHART_METRICS]: z.array(z.string()),
  [LocalStorageKey.UNIT_AVAILABILITY_CHART_GROUP_BY]: z.nativeEnum(TimeGranularity),
  [LocalStorageKey.UNIT_AVAILABILITY_CHART_DISPLAY_TYPE]: z.enum(['units', 'percent']),
  [LocalStorageKey.OCCUPANCY_CHART_OPTIONS]: chartOptionsSchema,
  [LocalStorageKey.PROPERTY_STATS_AGGREGATION_WINDOW_DAYS]: z.number(),
  [LocalStorageKey.COMPS_WEEKLY_INVENTORY_COMPS_AGGREGATED]: z.boolean(),
  [LocalStorageKey.COMPS_WEEKLY_INVENTORY_DATE_RANGE_DAYS]: z.number(),
  [LocalStorageKey.COMPS_MARKET_POSITION_ARE_COMPS_AGGREGATED]: z.boolean(),
  [LocalStorageKey.COMPS_MARKET_POSITION_DATE_RANGE_DAYS]: z.number(),
  [LocalStorageKey.MARKET_POSITION_CHART_COMPARE_MODE]: z.enum(['gross', 'effective']),
  [LocalStorageKey.COMPS_OCCUPANCY_COMPS_AGGREGATED]: z.boolean(),
  [LocalStorageKey.COMPS_OCCUPANCY_DATE_RANGE_DAYS]: z.number(),
  [LocalStorageKey.COMPS_SPECIALS_TIMELINE_DATE_RANGE_DAYS]: z.number(),
  [LocalStorageKey.DEMAND_CHART_OPTIONS]: chartOptionsSchema,
  [LocalStorageKey.RENEWALS_CHART_OPTIONS]: chartOptionsSchema,
  [LocalStorageKey.UPCOMING_RENEWALS_CHART_OPTIONS]: z.object({
    monthsAhead: z.union([z.literal(3), z.literal(6), z.literal(9), z.literal(12)]),
    dateGrouping: z.enum(['week', 'month']),
  }),
  [LocalStorageKey.CONVERSION_CHART_OPTIONS]: chartOptionsSchema,
  [LocalStorageKey.RENT_CHART_OPTIONS]: chartOptionsSchema,
  [LocalStorageKey.PRICE_HISTORY_RENT_CHART_OPTIONS]: chartOptionsSchema,
  [LocalStorageKey.PRICING_REVIEW_DATA]: z.record(
    z.string(), // Date of recommendation '2025-01-01'
    z.record(
      z.string(), // Property SID
      z.record(
        z.string(), // Unit Group Name or __meta
        z
          .union([
            // Meta object for property-level data (strict to prevent matching unit-group objects)
            z
              .object({
                overallReviewNotes: z.string().nullable().optional(),
                marketRentAdjustments: z
                  .array(
                    z.object({
                      unitSid: z.string(),
                      notes: z.string().nullable().optional(),
                      amount: z.number(),
                      removalType: z
                        .nativeEnum(MarketRentAdjustmentRemovalType)
                        .nullable()
                        .optional(),
                    })
                  )
                  .nullable()
                  .optional(),
              })
              .strict(),
            // Unit group pricing data
            z
              .object({
                bestRent: z.number().nullable().optional(),
                change: z.number().nullable().optional(),
                updateType: z.nativeEnum(PricingUpdateType).nullable().optional(),
                note: z.string().nullable().optional(),
              })
              .nullable(),
          ])
          .optional()
      )
    )
  ),
  [LocalStorageKey.NEVER]: z.never(),
  [LocalStorageKey.INCLUDE_DISABLED_PROPERTIES]: z.boolean(),
  [LocalStorageKey.INCLUDE_HIDDEN_PROPERTIES]: z.boolean(),
  [LocalStorageKey.OKTA_CLIENT_ID]: z.string(),
  [LocalStorageKey.OKTA_DOMAIN]: z.string(),
  [LocalStorageKey.OKTA_EXPECTED_EMAIL]: z.string(),
  [LocalStorageKey.LEAD_SOURCE_BREAKDOWN_USE_RAW]: z.boolean(),
  [LocalStorageKey.CHAT_DOCK_MODE]: z.enum(['centered', 'left', 'right']),
  [LocalStorageKey.CHAT_DOCK_WIDTH]: z.number(),
  [LocalStorageKey.ASK_RENTANA_LABEL_OVERRIDE]: z.string(),
} as const

export function useLocalStorage<K extends LocalStorageKey>(
  key: K,
  defaultValue: z.infer<(typeof localStorageKeyToSchema)[K]>
): [
  z.infer<(typeof localStorageKeyToSchema)[K]>,
  (value: z.infer<(typeof localStorageKeyToSchema)[K]>) => void,
]
export function useLocalStorage<K extends LocalStorageKey>(
  key: K
): [
  z.infer<(typeof localStorageKeyToSchema)[K]> | null,
  (value: z.infer<(typeof localStorageKeyToSchema)[K]>) => void,
]
export function useLocalStorage<K extends LocalStorageKey>(
  key: K,
  defaultValue?: z.infer<(typeof localStorageKeyToSchema)[K]>
): [
  z.infer<(typeof localStorageKeyToSchema)[K]> | null,
  (value: z.infer<(typeof localStorageKeyToSchema)[K]>) => void,
] {
  const [value, setValue] = useState<z.infer<(typeof localStorageKeyToSchema)[K]> | null>(() => {
    if (key === LocalStorageKey.NEVER) return null

    if (global?.window == undefined) {
      return null
    }
    const item = localStorage.getItem(key)
    if (item === null) return defaultValue ?? null

    try {
      const schema = localStorageKeyToSchema[key]
      let parsedValue: any

      if (schema._def.typeName === 'ZodNumber') {
        parsedValue = parseFloat(item)
      } else if (schema._def.typeName === 'ZodBoolean') {
        parsedValue = item === 'true'
      } else if (['ZodArray', 'ZodObject', 'ZodRecord'].includes(schema._def.typeName)) {
        parsedValue = JSON.parse(item)
      } else {
        parsedValue = item
      }

      return schema.parse(parsedValue)
    } catch (error) {
      console.error(`[useLocalStorage] validation failed for ${key}`, error)
      // Remove invalid value from localStorage
      localStorage.removeItem(key)
      return defaultValue ?? null
    }
  })

  const setLocalStorageValue = useCallback(
    (value: z.infer<(typeof localStorageKeyToSchema)[K]>) => {
      if (key === LocalStorageKey.NEVER) return

      let stringifiedValue: string
      try {
        if (['string', 'boolean', 'number'].includes(typeof value)) {
          stringifiedValue = String(value)
        } else if (value === null || value === undefined) {
          throw new Error('Cannot store null or undefined values')
        } else {
          stringifiedValue = JSON.stringify(value)
        }
        localStorage.setItem(key, stringifiedValue)
        setValue(value)
      } catch (error) {
        Sentry.captureMessage(`[useLocalStorage] Failed to stringify value for ${key}`, {
          level: 'error',
          extra: { error },
        })
      }
    },
    [key, setValue]
  )

  return [value, setLocalStorageValue]
}
